import { z } from 'zod';

/**
 * Formulardefinition eines Templates (SPEC Kap. 5.1/5.2).
 *
 * Aus genau diesem Schema erzeugt der Form-Renderer die Eingabemaske, der
 * Excel-Generator die Importvorlage und die Vollständigkeitsprüfung ihre
 * Regeln. Ein neues Feld im Editor darf nirgends Code-Änderungen erfordern.
 */

export const FELD_TYPEN = [
  'text',
  'mehrzeilig',
  'zahl',
  'waehrung',
  'datum',
  'uhrzeit',
  'auswahl',
  'mehrfachauswahl',
  'jaNein',
  'zustandsskala',
  'zaehlerstand',
  'fotos',
  'maengelblock',
  'schluesseltabelle',
  'signaturblock',
  'ueberschrift',
  'hinweistext',
  'wiederholbar',
] as const;

export type FeldTyp = (typeof FELD_TYPEN)[number];

/** Feldtypen ohne eigenen Datenwert — sie erscheinen nicht in `data`. */
export const REINE_ANZEIGE_TYPEN: readonly FeldTyp[] = ['ueberschrift', 'hinweistext'];

/** IDs müssen als Objektschlüssel und Handlebars-Pfad taugen. */
export const feldIdSchema = z
  .string()
  .min(1, 'ID darf nicht leer sein')
  .max(64, 'ID darf höchstens 64 Zeichen lang sein')
  .regex(
    /^[a-zA-Z][a-zA-Z0-9_]*$/,
    'ID muss mit einem Buchstaben beginnen und darf nur Buchstaben, Ziffern und _ enthalten',
  );

export const SICHTBAR_OPERATOREN = [
  'gleich',
  'ungleich',
  'enthaelt',
  'groesser',
  'kleiner',
  'gesetzt',
  'leer',
] as const;

export type SichtbarOperator = (typeof SICHTBAR_OPERATOREN)[number];

/** Einfache Bedingung auf ein anderes Feld desselben Abschnitts. */
export const sichtbarWennSchema = z.object({
  feld: feldIdSchema,
  operator: z.enum(SICHTBAR_OPERATOREN),
  wert: z.union([z.string(), z.number(), z.boolean()]).optional(),
});

export type SichtbarWenn = z.infer<typeof sichtbarWennSchema>;

const basisFeldFelder = {
  id: feldIdSchema,
  label: z.string().max(200).optional(),
  pflicht: z.boolean().optional(),
  hilfetext: z.string().max(500).optional(),
  nurLesen: z.boolean().optional(),
  sichtbarWenn: sichtbarWennSchema.optional(),
  /** Breite in einem 4-Spalten-Raster. Nur Layouthinweis, nie Pflicht. */
  breite: z.enum(['voll', 'halb', 'drittel']).optional(),
};

const optionenSchema = z.array(z.string().min(1)).min(1, 'Mindestens eine Option angeben').max(200);

export const ZUSTANDSSTUFEN_STANDARD = [
  'neuwertig',
  'gut',
  'gebraucht',
  'renovierungsbedürftig',
  'defekt',
] as const;

export const MANGEL_KATEGORIEN_STANDARD = [
  'Beschädigung',
  'Verschmutzung',
  'Verschleiß',
  'Fehlt',
  'Funktionsstörung',
  'Sonstiges',
] as const;

export const ZAEHLER_ARTEN_STANDARD = [
  'Strom',
  'Gas',
  'Wasser kalt',
  'Wasser warm',
  'Heizung',
] as const;

const textFeld = z.object({
  ...basisFeldFelder,
  typ: z.literal('text'),
  vorbelegung: z.string().optional(),
  maxLaenge: z.number().int().min(1).max(10_000).optional(),
  muster: z.string().optional(),
});

const mehrzeiligFeld = z.object({
  ...basisFeldFelder,
  typ: z.literal('mehrzeilig'),
  vorbelegung: z.string().optional(),
  zeilen: z.number().int().min(2).max(30).optional(),
  maxLaenge: z.number().int().min(1).max(20_000).optional(),
});

const zahlFeld = z.object({
  ...basisFeldFelder,
  typ: z.literal('zahl'),
  vorbelegung: z.number().optional(),
  min: z.number().optional(),
  max: z.number().optional(),
  dezimalstellen: z.number().int().min(0).max(6).optional(),
  einheit: z.string().max(16).optional(),
});

const waehrungFeld = z.object({
  ...basisFeldFelder,
  typ: z.literal('waehrung'),
  vorbelegung: z.number().optional(),
  min: z.number().optional(),
  max: z.number().optional(),
});

const datumFeld = z.object({
  ...basisFeldFelder,
  typ: z.literal('datum'),
  /** `heute` wird beim Anlegen aufgelöst, sonst ISO-Datum. */
  vorbelegung: z.union([z.literal('heute'), z.string()]).optional(),
  min: z.string().optional(),
  max: z.string().optional(),
});

const uhrzeitFeld = z.object({
  ...basisFeldFelder,
  typ: z.literal('uhrzeit'),
  vorbelegung: z.union([z.literal('jetzt'), z.string()]).optional(),
});

const auswahlFeld = z.object({
  ...basisFeldFelder,
  typ: z.literal('auswahl'),
  optionen: optionenSchema,
  vorbelegung: z.string().optional(),
  andereErlaubt: z.boolean().optional(),
});

const mehrfachauswahlFeld = z.object({
  ...basisFeldFelder,
  typ: z.literal('mehrfachauswahl'),
  optionen: optionenSchema,
  vorbelegung: z.array(z.string()).optional(),
  minAuswahl: z.number().int().min(0).optional(),
  maxAuswahl: z.number().int().min(1).optional(),
});

const jaNeinFeld = z.object({
  ...basisFeldFelder,
  typ: z.literal('jaNein'),
  vorbelegung: z.boolean().optional(),
});

const zustandsskalaFeld = z.object({
  ...basisFeldFelder,
  typ: z.literal('zustandsskala'),
  stufen: optionenSchema.optional(),
  vorbelegung: z.string().optional(),
});

const zaehlerstandFeld = z.object({
  ...basisFeldFelder,
  typ: z.literal('zaehlerstand'),
  arten: optionenSchema.optional(),
  dezimalstellen: z.number().int().min(0).max(4).optional(),
});

const fotosFeld = z.object({
  ...basisFeldFelder,
  typ: z.literal('fotos'),
  min: z.number().int().min(0).optional(),
  max: z.number().int().min(1).max(60).optional(),
});

const maengelblockFeld = z.object({
  ...basisFeldFelder,
  typ: z.literal('maengelblock'),
  kategorien: optionenSchema.optional(),
  fotosProMangelMax: z.number().int().min(0).max(20).optional(),
  kostenschaetzung: z.boolean().optional(),
});

const schluesseltabelleFeld = z.object({
  ...basisFeldFelder,
  typ: z.literal('schluesseltabelle'),
  arten: optionenSchema.optional(),
});

const signaturblockFeld = z.object({
  ...basisFeldFelder,
  typ: z.literal('signaturblock'),
  rollen: z.array(z.string().min(1)).min(1, 'Mindestens eine Rolle angeben'),
  mindestens: z.number().int().min(1).optional(),
});

const ueberschriftFeld = z.object({
  ...basisFeldFelder,
  typ: z.literal('ueberschrift'),
  text: z.string().min(1),
  stufe: z.union([z.literal(2), z.literal(3)]).optional(),
});

const hinweistextFeld = z.object({
  ...basisFeldFelder,
  typ: z.literal('hinweistext'),
  text: z.string().min(1),
  variante: z.enum(['info', 'warnung']).optional(),
});

/** Rekursiver Container: eine Zeilenliste mit eigenen Unterfeldern. */
export interface WiederholbarFeld {
  typ: 'wiederholbar';
  id: string;
  label?: string;
  pflicht?: boolean;
  hilfetext?: string;
  nurLesen?: boolean;
  sichtbarWenn?: SichtbarWenn;
  breite?: 'voll' | 'halb' | 'drittel';
  felder: Feld[];
  min?: number;
  max?: number;
  /** Vorbefüllung aus Stammdaten, z. B. `objekt.raeume`. */
  quelle?: string;
  /** Feld-ID, deren Wert als Zeilenüberschrift dient. */
  titelFeld?: string;
  hinzufuegenLabel?: string;
}

export type Feld =
  | z.infer<typeof textFeld>
  | z.infer<typeof mehrzeiligFeld>
  | z.infer<typeof zahlFeld>
  | z.infer<typeof waehrungFeld>
  | z.infer<typeof datumFeld>
  | z.infer<typeof uhrzeitFeld>
  | z.infer<typeof auswahlFeld>
  | z.infer<typeof mehrfachauswahlFeld>
  | z.infer<typeof jaNeinFeld>
  | z.infer<typeof zustandsskalaFeld>
  | z.infer<typeof zaehlerstandFeld>
  | z.infer<typeof fotosFeld>
  | z.infer<typeof maengelblockFeld>
  | z.infer<typeof schluesseltabelleFeld>
  | z.infer<typeof signaturblockFeld>
  | z.infer<typeof ueberschriftFeld>
  | z.infer<typeof hinweistextFeld>
  | WiederholbarFeld;

const wiederholbarFeld: z.ZodType<WiederholbarFeld> = z.lazy(() =>
  z.object({
    ...basisFeldFelder,
    typ: z.literal('wiederholbar'),
    felder: z.array(feldSchema).min(1, 'Ein wiederholbarer Block braucht mindestens ein Feld'),
    min: z.number().int().min(0).optional(),
    max: z.number().int().min(1).max(200).optional(),
    quelle: z.string().optional(),
    titelFeld: z.string().optional(),
    hinzufuegenLabel: z.string().optional(),
  }),
) as z.ZodType<WiederholbarFeld>;

export const feldSchema: z.ZodType<Feld> = z.lazy(() =>
  z.discriminatedUnion('typ', [
    textFeld,
    mehrzeiligFeld,
    zahlFeld,
    waehrungFeld,
    datumFeld,
    uhrzeitFeld,
    auswahlFeld,
    mehrfachauswahlFeld,
    jaNeinFeld,
    zustandsskalaFeld,
    zaehlerstandFeld,
    fotosFeld,
    maengelblockFeld,
    schluesseltabelleFeld,
    signaturblockFeld,
    ueberschriftFeld,
    hinweistextFeld,
    wiederholbarFeld as never,
  ]),
) as z.ZodType<Feld>;

export const ABSCHNITT_TYPEN = [
  'standard',
  'wiederholbar',
  'schluesseltabelle',
  'signaturblock',
] as const;

export type AbschnittTyp = (typeof ABSCHNITT_TYPEN)[number];

export const abschnittSchema = z
  .object({
    id: feldIdSchema,
    titel: z.string().min(1, 'Abschnitt braucht einen Titel').max(200),
    beschreibung: z.string().max(1000).optional(),
    typ: z.enum(ABSCHNITT_TYPEN).optional(),
    felder: z.array(feldSchema).optional(),
    /** Vorbefüllung aus Stammdaten, z. B. `objekt.raeume`. */
    quelle: z.string().optional(),
    min: z.number().int().min(0).optional(),
    max: z.number().int().min(1).max(200).optional(),
    titelFeld: z.string().optional(),
    hinzufuegenLabel: z.string().optional(),
    /** Nur für `signaturblock`. */
    rollen: z.array(z.string().min(1)).optional(),
    mindestens: z.number().int().min(1).optional(),
    /** Nur für `schluesseltabelle`. */
    arten: z.array(z.string().min(1)).optional(),
    sichtbarWenn: sichtbarWennSchema.optional(),
    seitenumbruchVor: z.boolean().optional(),
  })
  .superRefine((abschnitt, ctx) => {
    const typ = abschnitt.typ ?? 'standard';
    if ((typ === 'standard' || typ === 'wiederholbar') && (abschnitt.felder ?? []).length === 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['felder'],
        message: `Abschnitt "${abschnitt.titel}" braucht mindestens ein Feld`,
      });
    }
    if (typ === 'signaturblock' && (abschnitt.rollen ?? []).length === 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['rollen'],
        message: `Unterschriften-Abschnitt "${abschnitt.titel}" braucht mindestens eine Rolle`,
      });
    }
    if (typ === 'signaturblock' && abschnitt.mindestens !== undefined) {
      const rollen = abschnitt.rollen ?? [];
      if (abschnitt.mindestens > rollen.length) {
        ctx.addIssue({
          code: 'custom',
          path: ['mindestens'],
          message: 'Es können nicht mehr Unterschriften verlangt werden, als Rollen vorhanden sind',
        });
      }
    }
    const ids = (abschnitt.felder ?? []).map((feld) => feld.id);
    const doppelt = ids.filter((id, index) => ids.indexOf(id) !== index);
    if (doppelt.length > 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['felder'],
        message: `Feld-IDs müssen je Abschnitt eindeutig sein. Doppelt: ${[...new Set(doppelt)].join(', ')}`,
      });
    }
  });

export type Abschnitt = z.infer<typeof abschnittSchema>;

export const formSchemaSchema = z
  .object({
    version: z.number().int().min(1),
    titel: z.string().max(200).optional(),
    /**
     * Verlangt die Vorlage Unterschriften? Fehlt die Angabe, gilt `true` —
     * bestehende Vorlagen bleiben damit unverändert unterschriftspflichtig.
     *
     * Bei `false` bleiben die Unterschriften-Abschnitte nutzbar, das Protokoll
     * lässt sich aber auch ohne Unterschrift abschließen. Gedacht für
     * Protokolle, die nur für die eigene Objektakte entstehen — etwa eine
     * Handwerkerabnahme ohne anwesende Gegenseite.
     */
    unterschriftenErforderlich: z.boolean().optional(),
    abschnitte: z.array(abschnittSchema).min(1, 'Ein Formular braucht mindestens einen Abschnitt'),
  })
  .superRefine((formular, ctx) => {
    const ids = formular.abschnitte.map((abschnitt) => abschnitt.id);
    const doppelt = ids.filter((id, index) => ids.indexOf(id) !== index);
    if (doppelt.length > 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['abschnitte'],
        message: `Abschnitts-IDs müssen eindeutig sein. Doppelt: ${[...new Set(doppelt)].join(', ')}`,
      });
    }
  });

export type FormSchema = z.infer<typeof formSchemaSchema>;

/** Liefert alle Felder eines Abschnitts inklusive verschachtelter Blöcke. */
export const alleFelder = (felder: readonly Feld[]): Feld[] => {
  const ergebnis: Feld[] = [];
  for (const feld of felder) {
    ergebnis.push(feld);
    if (feld.typ === 'wiederholbar') {
      ergebnis.push(...alleFelder(feld.felder));
    }
  }
  return ergebnis;
};

/** Findet ein Feld über seinen Pfad `abschnittId.feldId`. */
export const findeFeld = (
  schema: FormSchema,
  abschnittId: string,
  feldId: string,
): Feld | undefined => {
  const abschnitt = schema.abschnitte.find((a) => a.id === abschnittId);
  if (!abschnitt) return undefined;
  return alleFelder(abschnitt.felder ?? []).find((feld) => feld.id === feldId);
};

/** Alle im Formular verwendeten Unterschriftsrollen, ohne Duplikate. */
export const signaturRollen = (schema: FormSchema): string[] => {
  const rollen = new Set<string>();
  for (const abschnitt of schema.abschnitte) {
    if ((abschnitt.typ ?? 'standard') === 'signaturblock') {
      for (const rolle of abschnitt.rollen ?? []) rollen.add(rolle);
    }
    for (const feld of alleFelder(abschnitt.felder ?? [])) {
      if (feld.typ === 'signaturblock') {
        for (const rolle of feld.rollen) rollen.add(rolle);
      }
    }
  }
  return [...rollen];
};

/**
 * Ob die Vorlage Unterschriften verlangt.
 *
 * Ohne Angabe: ja. Nur ein ausdrückliches `false` schaltet die Pflicht ab —
 * so bleiben alle vor dieser Einstellung gespeicherten Vorlagen wie bisher
 * unterschriftspflichtig.
 */
export const unterschriftenPflicht = (schema: FormSchema): boolean =>
  schema.unterschriftenErforderlich !== false;

/** Wie viele Unterschriften das Formular mindestens verlangt. */
export const mindestSignaturen = (schema: FormSchema): number => {
  if (!unterschriftenPflicht(schema)) return 0;
  let summe = 0;
  for (const abschnitt of schema.abschnitte) {
    if ((abschnitt.typ ?? 'standard') === 'signaturblock') {
      summe = Math.max(summe, abschnitt.mindestens ?? (abschnitt.rollen ?? []).length);
    }
    for (const feld of alleFelder(abschnitt.felder ?? [])) {
      if (feld.typ === 'signaturblock') {
        summe = Math.max(summe, feld.mindestens ?? feld.rollen.length);
      }
    }
  }
  return summe;
};

/**
 * Wie viele Unterschriften der Abschluss tatsächlich verlangt.
 *
 * Verlangt die Vorlage Unterschriften, ist mindestens eine nötig — auch wenn
 * kein Abschnitt eine Zahl vorgibt. Ist die Pflicht abgewählt, sind es null:
 * das Protokoll lässt sich dann ohne Unterschrift abschließen, vorhandene
 * Unterschriften werden trotzdem übernommen.
 *
 * Client und Lambda entscheiden über genau diese Funktion — die Regel darf
 * nicht an zwei Stellen unterschiedlich stehen.
 */
export const benoetigteSignaturen = (schema: FormSchema): number =>
  unterschriftenPflicht(schema) ? Math.max(1, mindestSignaturen(schema)) : 0;
